import { createSelector } from '@reduxjs/toolkit'

import { PRO_MODAL_PRODUCTS } from 'common/billing/constants'
import {
  getBillingState,
  getRootBillingState,
} from 'common/billing/entities/miscellaneous/selectors'
import { ProInfo } from 'common/billing/entities/pro/interfaces'
import { ProductType, ProProduct } from 'common/core/interfaces/products'
import { getAccountFlags } from 'common/core/selectors/appSelectors'
import { Products } from 'shared/api/requests/billing/schemas'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

/**
 * Local usage from products selectors to handle cycle deps.
 */
const getIsInboxEnabled = createSelector([getAccountFlags], (flags) =>
  Boolean(flags?.[AccountFlag.ENABLE_INBOX]),
)

export const getBillingProInfo = (state: RootState): ProInfo => state.billing.pro

export const getProSettings = createSelector([getRootBillingState], (billing) => billing.pro)

export const getProModalProducts = (state: RootState): ProProduct[] => {
  const products = state.billing.upgradeRequest.products || []

  return products.filter((product) =>
    PRO_MODAL_PRODUCTS.includes(product as ProProduct),
  ) as ProProduct[]
}

export const getProProducts = createSelector(getBillingProInfo, ({ products }): Products | null => {
  if (!products) {
    return null
  }

  return products
})

export const getActiveProProducts = createSelector(getProProducts, (products): ProProduct[] => {
  if (!products) {
    return []
  }
  return (Object.keys(products) as ProProduct[]).filter((name) => products[name].active)
})

export const getAvailableProProducts = createSelector([getIsInboxEnabled], (inboxEnabled) =>
  PRO_MODAL_PRODUCTS.filter((product) => product !== ProductType.INBOX || inboxEnabled),
)

/**
 *  This selector retrieves the fetching of billing pro settings.
 */
export const getIsLoadingProInfo = createSelector(
  [getBillingProInfo, getBillingState],
  (pro, billingState) => {
    const { fetchingPro } = billingState
    return fetchingPro || !Object.keys(pro).length
  },
)
